import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Download, X } from "lucide-react";
import type { ImageAsset } from "../api";

interface LightboxProps {
  image: ImageAsset;
  onClose: () => void;
  onDownload?: (image: ImageAsset) => void;
}

/**
 * The full-screen viewer. Two states and nothing else: fitted to the screen, or true 1:1 —
 * one image pixel to one device pixel — with native scrolling to pan. Click the image (or the
 * toggle) to switch, click anywhere else or press Escape to leave.
 */
export function Lightbox({ image, onClose, onDownload }: LightboxProps): ReactElement {
  const [actual, setActual] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);

  // Capture phase, and stop the event: a lightbox can sit above a modal or the inspector, both
  // of which also close on Escape, and one keypress should peel one layer.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // The page behind must not scroll while the image pans.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Entering 1:1 starts from the middle of the image rather than its top-left corner.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!actual || !viewport) return;
    viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) / 2;
    viewport.scrollTop = (viewport.scrollHeight - viewport.clientHeight) / 2;
  }, [actual]);

  // 1:1 means device pixels, so the CSS size divides out the pixel ratio. An asset whose
  // dimensions were never read (0×0) falls back to the browser's natural size instead.
  const hasSize = image.width > 0 && image.height > 0;
  const cssWidth = image.width / window.devicePixelRatio;
  const cssHeight = image.height / window.devicePixelRatio;

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="Image viewer">
      <div
        ref={viewportRef}
        className={actual ? "lightbox-viewport is-actual" : "lightbox-viewport"}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <img
          src={image.content_url}
          alt={image.prompt ?? ""}
          style={actual && hasSize ? { width: cssWidth, height: cssHeight } : undefined}
          onClick={() => setActual((current) => !current)}
        />
      </div>

      <div className="lightbox-chrome">
        <button
          type="button"
          className="lightbox-btn"
          aria-pressed={actual}
          title={actual ? "Fit to screen" : "One image pixel per device pixel"}
          onClick={() => setActual((current) => !current)}
        >
          {actual ? "Fit" : "1:1"}
        </button>
        {onDownload && (
          <button
            type="button"
            className="lightbox-btn"
            aria-label="Download"
            onClick={() => onDownload(image)}
          >
            <Download size={16} aria-hidden />
          </button>
        )}
        <button type="button" className="lightbox-btn" aria-label="Close" onClick={onClose}>
          <X size={16} aria-hidden />
        </button>
      </div>

      {hasSize && (
        <div className="lightbox-caption">
          <span className="stamp-value">
            {image.width}×{image.height}
          </span>
        </div>
      )}
    </div>
  );
}
